import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkoutPlanCard = ({ plan, viewMode = 'grid', isFavorite, onToggleFavorite, onQuickAssign, onSelect }) => {
  const difficultyColors = {
    beginner: 'text-green-600 bg-green-100',
    intermediate: 'text-yellow-600 bg-yellow-100',
    advanced: 'text-red-600 bg-red-100'
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        className="bg-card border border-border rounded-xl p-4 hover:shadow-lg transition-shadow"
        whileHover={{ y: -2 }}
      >
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="List" size={24} className="text-primary" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onSelect}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-foreground truncate">{plan?.name}</h3>
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onToggleFavorite();
                }}
                className="flex-shrink-0"
              >
                <Icon 
                  name={isFavorite ? 'Star' : 'Star'} 
                  size={18} 
                  className={isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}
                />
              </button>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
              {plan?.description}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors?.[plan?.difficulty]}`}>
                {plan?.difficulty}
              </span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon name="Clock" size={14} />
                <span>{plan?.durationMinutes} min</span>
              </div>
              {plan?.workoutPlanExercises && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="Dumbbell" size={14} />
                  <span>{plan?.workoutPlanExercises?.length} exercises</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Assign Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e?.stopPropagation();
              onQuickAssign();
            }}
            className="flex-shrink-0"
          >
            <Icon name="UserPlus" size={16} className="mr-2" />
            Assign
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow relative"
      whileHover={{ y: -4 }}
    >
      {/* Favorite Button */}
      <button
        onClick={(e) => {
          e?.stopPropagation();
          onToggleFavorite();
        }}
        className="absolute top-4 right-4 z-10"
      >
        <Icon 
          name={isFavorite ? 'Star' : 'Star'} 
          size={20} 
          className={isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}
        />
      </button>
      <div className="cursor-pointer" onClick={onSelect}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground text-lg mb-2">{plan?.name}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${difficultyColors?.[plan?.difficulty]}`}>
              {plan?.difficulty}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>{plan?.durationMinutes} min</span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {plan?.description}
        </p>
        <div className="space-y-3">
          {plan?.workoutPlanExercises && plan?.workoutPlanExercises?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                {plan?.workoutPlanExercises?.length} exercises
              </p>
              <div className="space-y-1">
                {plan?.workoutPlanExercises?.slice(0, 3)?.map((ex, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Icon name="CheckCircle" size={14} className="text-primary" />
                    <span className="text-foreground">{ex?.exercise?.name}</span>
                    {ex?.sets && ex?.reps && (
                      <span className="text-muted-foreground text-xs">
                        {ex?.sets}x{ex?.reps}
                      </span>
                    )}
                  </div>
                ))}
                {plan?.workoutPlanExercises?.length > 3 && (
                  <p className="text-xs text-muted-foreground ml-6">
                    +{plan?.workoutPlanExercises?.length - 3} more
                  </p>
                )}
              </div>
            </div>
          )}

          {plan?.tags && plan?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {plan?.tags?.map((tag, idx) => (
                <span key={idx} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                  {tag?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <Button 
          variant="default" 
          size="sm" 
          fullWidth
          onClick={(e) => {
            e?.stopPropagation();
            onQuickAssign();
          }}
        >
          <Icon name="UserPlus" size={16} className="mr-2" />
          Quick Assign to Client
        </Button>
      </div>
    </motion.div>
  );
};

export default WorkoutPlanCard;